import { Wallet, PieChart, Plus, Clock, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BottomNavProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

export default function BottomNav({ currentPage, onNavigate }: BottomNavProps) {
  const { t } = useTranslation();
  
  const navItems = [
    { id: 'home', icon: Home, label: t('bottom_nav.home') },
    { id: 'budget', icon: PieChart, label: t('bottom_nav.budget') },
    { id: 'add', icon: Plus, label: t('bottom_nav.add') },
    { id: 'rekening', icon: Wallet, label: t('bottom_nav.accounts') },
    { id: 'history', icon: Clock, label: t('bottom_nav.history') },
  ];

  const leftItems = navItems.filter(item => item.id === 'home' || item.id === 'budget');
  const rightItems = navItems.filter(item => item.id === 'rekening' || item.id === 'history');
  const addItem = navItems.find(item => item.id === 'add');

  const renderNavButton = (item: typeof navItems[0]) => {
    const Icon = item.icon;
    const isActive = currentPage === item.id;

    return (
      <button
        key={item.id}
        onClick={() => onNavigate(item.id)}
        className={`flex flex-col items-center justify-center py-2 px-1 sm:px-3 md:px-4 min-w-0 ${
          isActive ? 'text-[#077f5d] flex-[1.3]' : 'text-[#077f5d] opacity-50 flex-1'
        }`}
        style={{ transition: 'none' }}
      >
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 ${isActive ? 'stroke-[3]' : 'stroke-[2]'}`} />
        <span
          className={`text-[9px] sm:text-[10px] mt-1 w-full text-center font-mono uppercase tracking-wide ${
            isActive ? 'font-bold whitespace-nowrap' : 'font-medium truncate'
          }`}
          style={isActive ? { fontFamily: "'Press Start 2P', monospace", fontSize: '8px', lineHeight: '1.4' } : {}}
        >
          {item.label}
        </span>
      </button>
    );
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#077f5d] px-2 sm:px-4 md:px-6 py-3 md:py-4"
      style={{ boxShadow: '0 -2px 0 #077f5d', transition: 'none' }}
    >
      <div className="max-w-lg mx-auto flex items-center gap-1 md:gap-2">
        {/* Left Items */}
        <div className="flex flex-1 gap-1 md:gap-2 justify-end">
          {leftItems.map(renderNavButton)}
        </div>

        {/* Center Add Button - Fixed Position */}
        {addItem && (
          <button
            onClick={() => onNavigate(addItem.id)}
            className="flex flex-col items-center justify-center -mt-10 md:-mt-12 flex-shrink-0 mx-2"
            style={{ transition: 'none' }}
            onMouseEnter={(e) => {
              const div = e.currentTarget.querySelector('div');
              if (div) {
                div.style.boxShadow = '2px 2px 0 #077f5d';
                div.style.transform = 'translate(-1px, -1px)';
              }
            }}
            onMouseLeave={(e) => {
              const div = e.currentTarget.querySelector('div');
              if (div) {
                div.style.boxShadow = '4px 4px 0 #077f5d';
                div.style.transform = 'translate(0, 0)';
              }
            }}
          >
            <div
              className="w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 bg-[#3cbe8c] border-4 border-[#077f5d] flex items-center justify-center"
              style={{ boxShadow: '4px 4px 0 #077f5d', transition: 'none' }}
            >
              <Plus className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 text-white stroke-[2.5]" />
            </div>
          </button>
        )}

        {/* Right Items */}
        <div className="flex flex-1 gap-1 md:gap-2 justify-start">
          {rightItems.map(renderNavButton)}
        </div>
      </div>
    </nav>
  );
}
